import React from 'react';
import { Avatar } from 'antd';
import {
  JavaOriginal,
  SpringOriginal,
  MavenOriginal,
  ApachekafkaOriginal,
  ArduinoOriginal,
  COriginal,
  CplusplusOriginal,
  CsharpOriginal,
  ReactOriginal,
  JavascriptOriginal,
  TypescriptOriginal,
  NodejsOriginal,
  PythonOriginal,
  GitOriginal,
  GithubOriginal,
  PostmanOriginal,
  LinuxOriginal,
} from 'devicons-react';
import { FaAws } from 'react-icons/fa';
import { getAlbumPath } from '../../utils/getPublicPath';
import { PersonalProjects } from './personalProjects';
import type { Playlist, Song } from '../../interfaces/types';

export enum SkillsTypesEnum {
  CURRENT = 'CURRENT',
}

const AwsIcon: React.FC = () => {
  const Icon = FaAws as unknown as React.ComponentType<any>;
  return <Icon size="1.4rem" style={{ color: '#fff' }} />;
};

/* ---------------- CSS-only icons (used only where no real brand icon exists) ---------------- */
const base: React.CSSProperties = {
  display: 'inline-block',
  width: '1.8rem',
  height: '1.8rem',
  position: 'relative',
  borderRadius: 6,
  background: 'transparent',
};

const IconIot: React.FC = () => {
  const rod: React.CSSProperties = { position: 'absolute', left: 'calc(50% - 1px)', top: '6px', width: '2px', height: '12px', background: '#0d3d2a' };
  const node: React.CSSProperties = { position: 'absolute', left: 'calc(50% - 3px)', top: '4px', width: '6px', height: '6px', background: '#0d3d2a', borderRadius: '50%', boxShadow: '0 0 0 3px #3ae08f' };
  const wave = (o: number): React.CSSProperties => ({
    position: 'absolute', left: `calc(50% - ${8 + o}px)`, top: 6,
    width: 16 + o * 2, height: 16 + o * 2, borderRadius: '50%', border: '2px solid #3ae08f', opacity: 0.75 - o * 0.15,
  });
  return (
    <span style={{ ...base, background: 'linear-gradient(#3ae08f, #0fb873)', borderRadius: '50%' }}>
      <span style={rod} /><span style={node} />
      <span style={wave(0)} /><span style={wave(3)} />
    </span>
  );
};

const IconMicroservices: React.FC = () => {
  const box = (x: number, y: number): React.CSSProperties => ({ position: 'absolute', left: x, top: y, width: 8, height: 8, borderRadius: 2, background: '#fff' });
  const line = (s: React.CSSProperties): React.CSSProperties => ({ position: 'absolute', background: '#fff', ...s });
  return (
    <span style={{ ...base, background: 'linear-gradient(#7bd3ff, #0EA5E9)', borderRadius: '50%' }}>
      <span style={box(4, 4)} /><span style={box(12, 4)} /><span style={box(4, 12)} /><span style={box(12, 12)} />
      <span style={line({ left: 8, top: 8, width: 8, height: 2 })} />
      <span style={line({ left: 8, top: 8, width: 2, height: 8 })} />
      <span style={line({ left: 8, top: 14, width: 8, height: 2 })} />
      <span style={line({ left: 14, top: 8, width: 2, height: 8 })} />
    </span>
  );
};

/* ---------------- Skills (real brand icons, no links; descriptions included) ---------------- */
type CssSkill = {
  text: string;
  description: string;
  icon: React.FC<any>;
  /** Circular backdrop behind the icon — needed for icons that render dark/monochrome */
  bg?: string;
};

const SKILL_TAGS: readonly CssSkill[] = [
  /* --- Java / backend --- */
  { text: 'Java',            description: 'JVM, collections, concurrency, streams, REST services in production.',    icon: () => <JavaOriginal size="1.8rem" /> },
  { text: 'Spring Boot',     description: 'Production-ready microservices with auto-config and starters.',           icon: () => <SpringOriginal size="1.8rem" /> },
  { text: 'Spring Security', description: 'Authentication/authorization for Java apps: JWT, OAuth2, RBAC.',          icon: () => <SpringOriginal size="1.8rem" /> },
  { text: 'Spring AI',       description: 'Spring-first LLM apps, model clients, prompts, vector stores.',           icon: () => <SpringOriginal size="1.8rem" /> },
  { text: 'Maven',           description: 'Build lifecycle, dependency management, multi-module projects.',           icon: () => <MavenOriginal size="1.8rem" />, bg: '#f5f5f5' },
  { text: 'Apache Kafka',    description: 'Event streaming, topics/partitions, consumer groups & exactly-once.',     icon: () => <ApachekafkaOriginal size="1.8rem" />, bg: '#f5f5f5' },
  { text: 'Microservices',   description: 'Service boundaries, API gateways, discovery, resilience patterns.',       icon: IconMicroservices },

  /* --- Web / scripting --- */
  { text: 'JavaScript',      description: 'ES6+, async/await, modules, DOM APIs, tooling and testing.',              icon: () => <JavascriptOriginal size="1.8rem" /> },
  { text: 'TypeScript',      description: 'Type-safe code, interfaces, generics across React and Node projects.',    icon: () => <TypescriptOriginal size="1.8rem" /> },
  { text: 'React',           description: 'Components, hooks, context, state mgmt, performant SPA patterns.',        icon: () => <ReactOriginal size="1.8rem" /> },
  { text: 'Node.js',         description: 'REST APIs, tooling, and backend services on the JS runtime.',             icon: () => <NodejsOriginal size="1.8rem" /> },
  { text: 'Python',          description: 'Scripting, automation, and data-oriented tasks from job simulations.',    icon: () => <PythonOriginal size="1.8rem" /> },

  /* --- Embedded / IoT --- */
  { text: 'Arduino',         description: 'MCU prototyping, sensors/actuators, serial comms, embedded loops.',       icon: () => <ArduinoOriginal size="1.8rem" /> },
  { text: 'IoT',             description: 'Edge devices, MQTT/HTTP, telemetry, cloud ingestion & dashboards.',       icon: IconIot },
  { text: 'C',               description: 'Low-level memory, pointers, systems programming, cross-platform builds.', icon: () => <COriginal size="1.8rem" /> },
  { text: 'C++',             description: 'OOP & STL, RAII, templates, performance and modern C++ paradigms.',       icon: () => <CplusplusOriginal size="1.8rem" /> },
  { text: 'C#',              description: '.NET ecosystem, async/await, LINQ, cross-platform apps & APIs.',          icon: () => <CsharpOriginal size="1.8rem" /> },

  /* --- Tooling / platform --- */
  { text: 'Git',             description: 'Branching, rebasing, resolving conflicts across team workflows.',         icon: () => <GitOriginal size="1.8rem" /> },
  { text: 'GitHub',          description: 'Repos, issues, Actions, and Pages for shipping and hosting projects.',     icon: () => <GithubOriginal size="1.8rem" />, bg: '#f5f5f5' },
  { text: 'Postman',         description: 'Designing, testing, and documenting REST APIs during job simulations.',   icon: () => <PostmanOriginal size="1.8rem" /> },
  { text: 'AWS',             description: 'Cloud fundamentals covered during the Deloitte technology simulation.',   icon: AwsIcon, bg: 'linear-gradient(135deg,#FF9900,#e68a00)' },
  { text: 'Linux',           description: 'Shell workflows, permissions, and process management day to day.',        icon: () => <LinuxOriginal size="1.8rem" />, bg: '#f5f5f5' },
];

/* ---------------- Map to Song (with experience years) ---------------- */
const mapTag = (tag: CssSkill, type: SkillsTypesEnum): Song => {
  const Icon = tag.icon;

  // Set experience years per your rules
  let experience = '2 years';
  if (tag.text === 'Java') experience = '3 years';
  if (['Apache Kafka', 'Microservices', 'TypeScript', 'Node.js', 'Python', 'Git', 'GitHub', 'Postman', 'AWS', 'Linux'].includes(tag.text)) {
    experience = '1 year';
  }

  return {
    types: [type],
    skills: [],
    name: tag.text,
    length: experience, // experience shown in UI
    description: tag.description, // revealed on click/expand
    relatedSongs: PersonalProjects.songs.filter((song) =>
      song.skills?.some?.((s) => s.text === tag.text)
    ),
    avatar: (
      <Avatar
        shape="square"
        size="large"
        style={{
          background: tag.bg ?? 'transparent',
          fontSize: '1.8rem',
          marginRight: 15,
          borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          padding: 2,
        }}
        icon={<Icon />}
      />
    ),
  };
};

/* ---------------- Export SKILLS_SONGS ---------------- */
export const SKILLS_SONGS: Song[] = SKILL_TAGS.map((tag) => mapTag(tag, SkillsTypesEnum.CURRENT));

/* ---------------- Playlist ---------------- */
export const Skills: Playlist = {
  name: 'SKILLS',
  color: '#4f596e',
  songs: SKILLS_SONGS,
  defaultFilter: SkillsTypesEnum.CURRENT,
  description:
    'Full-stack + IoT skills with click-to-reveal descriptions, shown with real brand icons.',
  filters: [SkillsTypesEnum.CURRENT],
  getImage(lang: string) {
    return getAlbumPath('Skills-cover', lang, 'png');
  },
};
